import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Alert,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from 'notistack';
import api from '../api';

interface Organization {
  id: number;
  name: string;
  address: string | null;
  contacts: string | null;
  _count: { employees: number };
}

const emptyForm = { name: '', address: '', contacts: '' };

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Organization | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const fetchOrgs = async () => {
    try {
      const { data } = await api.get('/organizations');
      setOrgs(data);
    } catch (err: any) {
      enqueueSnackbar(t('organizations.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrgs(); }, []);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = t('organizations.nameRequired');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { name: form.name, address: form.address || null, contacts: form.contacts || null };
      if (editingId) {
        await api.put(`/organizations/${editingId}`, payload);
        enqueueSnackbar(t('organizations.snackbarUpdated'), { variant: 'success' });
      } else {
        await api.post('/organizations', payload);
        enqueueSnackbar(t('organizations.snackbarCreated'), { variant: 'success' });
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchOrgs();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingId(org.id);
    setForm({ name: org.name, address: org.address || '', contacts: org.contacts || '' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/organizations/${deleteConfirm.id}`);
      enqueueSnackbar(t('organizations.snackbarDeleted'), { variant: 'success' });
      setDeleteConfirm(null);
      fetchOrgs();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #5ee7f1 20%, #8b8bff 80%)'
                : 'linear-gradient(135deg, #22c3d1 20%, #5b5bf6 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(94,231,241,0.55)'
                : '0 8px 20px -8px rgba(34,195,209,0.4)',
            }}
          >
            <BusinessIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('organizations.intro')} · {t('organizations.total')}: <strong style={{ color: 'var(--text)' }}>{orgs.length}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingId(null); setForm(emptyForm); setFormErrors({}); setDialogOpen(true); }}
        >
          {t('common.add')}
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('organizations.tableName')}</TableCell>
                <TableCell>{t('organizations.tableAddress')}</TableCell>
                <TableCell>{t('organizations.tableContacts')}</TableCell>
                <TableCell align="center">{t('organizations.tableEmployeeCount')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                    {t('organizations.noOrganizations')}
                  </TableCell>
                </TableRow>
              ) : (
                orgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{org.name}</TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{org.address || '—'}</TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{org.contacts || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={org._count.employees}
                        size="small"
                        sx={{
                          background: isDark ? 'rgba(139,139,255,0.15)' : 'rgba(91,91,246,0.1)',
                          color: 'var(--text)',
                          fontWeight: 600,
                          border: 'none',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(org)} aria-label={t('common.edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Tooltip
                        title={
                          org._count.employees > 0
                            ? t('organizations.deleteDisabled')
                            : t('common.delete')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirm(org)}
                            disabled={org._count.employees > 0}
                            aria-label={t('common.delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('organizations.editOrganization') : t('organizations.addOrganization')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('organizations.formName')}
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label={t('organizations.formAddress')}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('organizations.formContacts')}
            value={form.contacts}
            onChange={(e) => setForm({ ...form, contacts: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('organizations.deleteOrganization')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)' }}>
            {t('organizations.deleteConfirm', { name: deleteConfirm?.name })}
          </Typography>
          {deleteConfirm && deleteConfirm._count.employees > 0 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
              {t('organizations.deleteWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!!deleteConfirm && deleteConfirm._count.employees > 0}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}